import { APICompsSchema } from "@unit/apis";
import { isDateBefore } from "@unit/utility";
import { signOut } from "firebase/auth";
import { useAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import localforage from "localforage";
import React, { useCallback, useEffect } from "react";
import { useQueries } from "react-query";

import InitialScreenContainer from "@/components/common/InitialScreenContainer";
import { BackdropProgress } from "@/components/provider/BackdropProgress";
import { useAreaApi } from "@/custom-hooks/apis/use-area-api";
import { useGenreApi } from "@/custom-hooks/apis/use-genre-api";
import { useStaffApi } from "@/custom-hooks/apis/use-staff-api";
import { useAppSnackbar } from "@/custom-hooks/use-app-snackbar";
import { useAuthentication } from "@/custom-hooks/use-authentication";
import { useResetAuth } from "@/custom-hooks/use-reset-auth";
import { STAFF_STORE_LIST_MONTHS_OFFSET } from "@/global-state/app-config";
import { firebaseGetAuth } from "@/global-state/firebase-settings";
import { authUserAtom, idTokenAtom, selectStoreAtom } from "@/global-state/jotai-atom";
import { QueryKeys } from "@/global-state/react-query-keys";
import { storeSelectionPersistenceAtom, validSelectedStoreAtom } from "@/global-state/store-selection-persistence";

type Props = {
  children: React.ReactNode;
};

const AuthProvider: React.FC<Props> = (props) => {
  const { setAppSnackbar } = useAppSnackbar();
  const { resetAuth } = useResetAuth();
  const { syncIdToken } = useAuthentication();
  const [authUser] = useAtom(authUserAtom);
  const [idToken] = useAtom(idTokenAtom);

  const restoreStaffStore = useAtomCallback(
    useCallback(
      async (get, set, staff: APICompsSchema["StaffObject"]) => {
        const previousUserId = get(authUserAtom)?.id;
        const beforeRead = get(storeSelectionPersistenceAtom);
        const version = beforeRead.operationId;
        set(authUserAtom, staff);
        const storeId = await localforage.getItem("selected_store_id").catch(() => null);
        // A later profile response/logout wins. Never restore a transient or known inconsistent persisted ID.
        if (get(authUserAtom) !== staff) return;
        const state = get(storeSelectionPersistenceAtom);
        const selectableStores = staff.stores.filter(
          (store) => !isDateBefore(store.contractEndDate, undefined, STAFF_STORE_LIST_MONTHS_OFFSET),
        );
        const protectedSelection =
          beforeRead.pending ||
          beforeRead.needsRepair ||
          state.pending ||
          state.needsRepair ||
          state.operationId !== version;
        const current = previousUserId === staff.id ? get(validSelectedStoreAtom) : undefined;
        const targetStore =
          (protectedSelection ? current : selectableStores.find((store) => store.id === storeId)) ||
          selectableStores[0];
        if (!targetStore) {
          setAppSnackbar("どのストアにも所属していないユーザーです", { error: true });
          await resetAuth();
          return;
        }
        set(selectStoreAtom, targetStore);
      },
      [resetAuth, setAppSnackbar],
    ),
  );

  const { getAreaList } = useAreaApi();
  const { getGenreList } = useGenreApi();
  const { getStaffProfile } = useStaffApi();

  const handleWindowFocusChange = async () => {
    if (!document.hidden) {
      await syncIdToken();
    }
  };
  useEffect(() => {
    document.addEventListener("visibilitychange", handleWindowFocusChange);
    return () => {
      document.removeEventListener("visibilitychange", handleWindowFocusChange);
    };
  }, []);

  useEffect(() => {
    (async () => {
      const accessToken = await syncIdToken();
      if (!accessToken) {
        return await resetAuth();
      }
    })();
  }, [idToken]);

  useEffect(() => {
    if (!authUser?.id) {
      return;
    }
    const resetInterval = setInterval(() => syncIdToken(), 10 * 60 * 1000); // 10分間
    return () => clearInterval(resetInterval);
  }, [authUser?.id]);

  // GlobalStateとして扱うデータをAPIから取得
  const initApiResults = useQueries([
    {
      queryKey: QueryKeys.getAreaList,
      queryFn: () => getAreaList(),
    },
    {
      queryKey: QueryKeys.getGenreList,
      queryFn: () => getGenreList(),
    },
    {
      queryKey: [QueryKeys.getStaffProfile],
      queryFn: () => getStaffProfile(),
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onSuccess: async (res: any) => {
        await restoreStaffStore(res.object as APICompsSchema["StaffObject"]);
      },
      onError: async (e: any) => {
        const status = e?.response?.status;
        const errorObj = e?.response?.data;
        console.error(errorObj?.message || errorObj?.devMessage);
        if (status === 401) {
          await signOut(firebaseGetAuth);
        }
        await resetAuth();
      },
      enabled: !!idToken,
    },
  ]);

  const loading = initApiResults[0].isLoading || initApiResults[1].isLoading || initApiResults[2].isLoading;

  if (loading && !authUser?.id) {
    return (
      <BackdropProgress open>
        <InitialScreenContainer />
      </BackdropProgress>
    );
  }

  return <>{props.children}</>;
};

export default AuthProvider;
